import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, MapPin, Calendar, Filter, Loader2 } from 'lucide-react';
import SEO, { getBreadcrumbSchema } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OffersList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    location: searchParams.get('location') || '',
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API}/categories`);
        if (res.ok) setCategories(await res.json());
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchOffers = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.category && filters.category !== 'all') params.append('category', filters.category);
        if (filters.location) params.append('location', filters.location);
        
        const res = await fetch(`${API}/offers?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setOffers(data.offers || []);
          setTotal(data.total || 0);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const actualValue = value === 'all' ? '' : value;
    const newFilters = { ...filters, [key]: actualValue };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    if (newFilters.category) params.set('category', newFilters.category);
    if (newFilters.location) params.set('location', newFilters.location);
    setSearchParams(params);
  };

  // Get current category name for SEO
  const currentCategory = categories.find(c => c.id === filters.category);
  const categoryName = currentCategory?.name || '';
  const locationName = filters.location ? filters.location.charAt(0).toUpperCase() + filters.location.slice(1) : '';
  
  // Dynamic SEO based on filters
  const seoTitle = categoryName && locationName 
    ? `${categoryName} - ${locationName}` 
    : categoryName 
    ? categoryName 
    : locationName 
    ? `Ogłoszenia budowlane - ${locationName}` 
    : 'Giełda Ogłoszeń - wszystkie ogłoszenia budowlane';
    
  const seoDescription = categoryName 
    ? `Przeglądaj ${total} ogłoszeń w kategorii ${categoryName}${locationName ? ` w ${locationName}` : ''}. Znajdź zlecenia budowlane i remontowe w całej Polsce.`
    : `Przeglądaj ${total} aktywnych ogłoszeń budowlanych${locationName ? ` w ${locationName}` : ' w całej Polsce'}. Zlecenia remontowe, elektryka, hydraulika i więcej.`;

  const breadcrumbs = [
    { name: 'Strona główna', path: '/' },
    { name: 'Giełda Ogłoszeń', path: '/offers' },
    ...(categoryName ? [{ name: categoryName, path: `/offers?category=${filters.category}` }] : [])
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="offers-list-page">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        canonical={`/offers${filters.category ? `?category=${filters.category}` : ''}${filters.location ? `${filters.category ? '&' : '?'}location=${filters.location}` : ''}`}
        structuredData={getBreadcrumbSchema(breadcrumbs)}
      />
      
      <div className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading font-bold text-3xl text-white mb-2">Giełda Ogłoszeń</h1>
          <p className="text-slate-400">{total} aktywnych ogłoszeń</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="mb-8">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-slate-600" />
              <span className="font-heading font-bold text-slate-900">Filtry</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select value={filters.category || 'all'} onValueChange={(v) => handleFilterChange('category', v)}>
                <SelectTrigger data-testid="filter-category">
                  <SelectValue placeholder="Kategoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie kategorie</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input placeholder="Lokalizacja" value={filters.location} onChange={(e) => handleFilterChange('location', e.target.value)} data-testid="filter-location" />
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : offers.length === 0 ? (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-slate-700 mb-2">Brak ogłoszeń</h3>
            <p className="text-slate-500">Nie znaleziono ogłoszeń spełniających kryteria</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((offer) => (
              <Link key={offer.offer_id} to={`/offers/${offer.offer_id}`} data-testid={`offer-card-${offer.offer_id}`}>
                <Card className="h-full border border-slate-200 hover:border-orange-500/50 transition-all card-hover">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <Badge variant="secondary" className="text-xs">
                        {categories.find(c => c.id === offer.category)?.name || offer.category}
                      </Badge>
                      {offer.responses_count > 0 && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">{offer.responses_count} ofert</Badge>
                      )}
                    </div>
                    <h3 className="font-heading font-bold text-lg text-slate-900 mb-2 line-clamp-2">{offer.title}</h3>
                    <p className="font-body text-sm text-slate-600 mb-4 line-clamp-3">{offer.description}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{offer.location}</span>
                      {offer.deadline && (
                        <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(offer.deadline).toLocaleDateString('pl-PL')}</span>
                      )}
                    </div>
                    <span className="text-xs text-slate-400">{new Date(offer.created_at).toLocaleDateString('pl-PL')}</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OffersList;
